import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, generatePath } from 'react-router-dom';

import { routes } from 'client-config';
import { legacyColors } from 'constants-colors';
import type { CostOffsetCode } from 'domains-billing-types';
import { GroupType } from 'graphql-query-contracts';
import { useChannelAdminContext } from 'hooks';
import { useDeleteCostOffsetCode } from 'react-hooks';

import { getDeleteCostOffsetCodeErrorMessage } from './getDeleteCostOffsetCodeErrorMessage';

export interface CostOffsetCodeAction {
  label: string;
  onSelect: (row: CostOffsetCode) => void;
}

interface UseGetActionsForCostOffsetCodesParams {
  refetch: () => Promise<void> | void;
}

export function useGetActionsForCostOffsetCodes({
  refetch,
}: UseGetActionsForCostOffsetCodesParams): CostOffsetCodeAction[] {
  const { t } = useTranslation();
  const history = useHistory();
  const { channel } = useChannelAdminContext();
  const channelSlug = channel?.slug || '';

  const { deleteCostOffsetCode } = useDeleteCostOffsetCode();

  const onDelete = useCallback(
    async (row: CostOffsetCode) => {
      try {
        await window.Alert.confirm({
          title: t('abp.productsServices.deleteCostOffsetCode.title', {
            code: row.code,
          }),
          message: t(
            'abp.productsServices.deleteCostOffsetCode.confirmMessage'
          ),
          confirmText: t('abp.productsServices.deleteCostOffsetCode.confirm'),
          cancelText: t('abp.productsServices.deleteCostOffsetCode.cancel'),
          confirmButtonStyle: {
            backgroundColor: legacyColors.neutral.red7200,
            borderColor: legacyColors.neutral.red7200,
            color: legacyColors.neutral.white,
          },
        });
      } catch {
        return;
      }

      try {
        await deleteCostOffsetCode({
          id: row.id,
          groupId: channel?._id ?? '',
          groupType: GroupType.GroupTypeActivateChannel,
        });
      } catch (error) {
        try {
          await window.Alert.confirm({
            title: t('abp.productsServices.deleteCostOffsetCode.errorTitle', {
              code: row.code,
            }),
            message: getDeleteCostOffsetCodeErrorMessage(error, t),
            confirmText: t('abp.productsServices.deleteCostOffsetCode.errorOk'),
          });
        } catch {
          // User dismissed the alert
        }

        return;
      }

      window.Toast.show(t('abp.productsServices.deleteCostOffsetCode.success'));

      try {
        await refetch();
      } catch {
        // Refresh failed silently; the list will be stale until next navigation
      }
    },
    [channel?._id, deleteCostOffsetCode, refetch, t]
  );

  return [
    {
      label: t('abp.productsServices.costOffsetCodes.actions.edit'),
      onSelect: (row: CostOffsetCode) => {
        history.push(
          generatePath(routes.channelAdminEditCostOffsetCodeForm, {
            id: channelSlug,
            costOffsetCodeId: row.id,
          })
        );
      },
    },
    {
      label: t('abp.productsServices.costOffsetCodes.actions.delete'),
      onSelect: onDelete,
    },
  ];
}
